import { fetchGoodsList } from '@/api/product';
import {
  IGoodsListPageInitialData,
  IGroupInfo,
  IProductListApiResponse,
  IProductListItem,
} from '@/api/types/product';
import GoodsFilter from '@/app/goods-list/components/GoodsFilter';
import ProductList from '@/app/goods-list/components/ProductList';
import SortFilterBar from '@/app/goods-list/components/SortFilterBar';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EProductImageType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { transformFilterParams } from '@/utils/goods-list-utils';
import { isEmpty } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTrack } from '../../hooks/useTrack';
import Header from './components/Header';

export interface GoodsListPartProps {
  goodsListData: any;
  initialData: IGoodsListPageInitialData | null;
  searchParams: {
    sort?: string;
    filter?: string;
    adp?: string;
    page?: string;
  };
}

export default function GoodsListPart({
  goodsListData,
  initialData,
  searchParams,
}: GoodsListPartProps) {
  const router = useRouter();
  const isMobileScreen = useStore((state) => state.isMobileScreen);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const currency = useStore((state) => state.currency);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);

  // 解析当前URL参数
  const currentSort = searchParams?.sort || DEFAULT_SORT_VALUE;
  const currentPage = parseInt(searchParams?.page || '1', 10);

  // 使用useMemo稳定currentFilter的引用，避免无限循环
  const currentFilter = useMemo<Record<string, any>>(() => {
    try {
      return searchParams?.filter ? JSON.parse(searchParams.filter) : {};
    } catch {
      return {};
    }
  }, [searchParams?.filter]);

  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);

  // 弹窗模式下的临时筛选状态
  const [tempFilter, setTempFilter] = useState<Record<string, any>>({});

  // 基础状态（来自服务端）
  const [groupInfo, setGroupInfo] = useState<IGroupInfo>(initialData.group);
  const [listData, setListData] = useState<IProductListItem[]>(initialData.list);
  const [total, setTotal] = useState(initialData.total);
  const [allTotal, setAllTotal] = useState(initialData.all_total);
  const [imageRule, setImageRule] = useState(initialData.image_rule);
  const [imageHoverRule, setImageHoverRule] = useState(initialData.hover_image_rule);
  const [noData, setNoData] = useState(initialData.noData);

  // UI相关状态
  const [loading, setLoading] = useState(false);

  const [noMoreShow, setNoMoreShow] = useState(initialData.noMoreShow);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [column, setColumn] = useState(2);
  const [infiniteScrollPage, setInfiniteScrollPage] = useState(currentPage);

  // 判断页面类型
  const isNewin = groupInfo?.page_template?.id === 3;
  const isAcc = groupInfo?.page_template?.id === 5;
  const listId = goodsListData.id;
  const limit = 36;

  // 计算筛选总数
  const filterTotal = Object.keys(currentFilter).reduce((_total, key) => {
    const value = currentFilter[key];
    if (['temple', 'frame_width'].includes(key)) return _total;
    if (Array.isArray(value)) return _total + value.length;
    return value ? _total + 1 : _total;
  }, 0);

  // 初始化 expandedSections - 默认全部展开
  useEffect(() => {
    const product_filter_new = publicResourceData?.config_names?.product_filter_new;
    if (product_filter_new && Array.isArray(product_filter_new) && product_filter_new.length > 0) {
      const initialExpandedSections: Record<string, boolean> = {};
      product_filter_new.forEach((item: any) => {
        if (item.attribute_code) {
          initialExpandedSections[item.attribute_code] = true; // 默认展开
        }
      });
      setExpandedSections(initialExpandedSections);
    }
  }, [publicResourceData]);

  // 监听initialData变化，更新客户端状态
  useEffect(() => {
    const newGroupInfo = initialData.group;
    setGroupInfo(newGroupInfo);
    setListData(initialData.list);
    setTotal(initialData.total);
    setAllTotal(initialData.all_total);
    setImageRule(
      initialData.image_rule || [
        EProductImageType.PRODUCT,
        EProductImageType.MATERIAL,
        EProductImageType.CELEBRITY,
      ],
    );
    setImageHoverRule(
      initialData.hover_image_rule || [
        EProductImageType.MATERIAL,
        EProductImageType.CELEBRITY,
        EProductImageType.PRODUCT,
      ],
    );
    setNoData(initialData.noData);
    setNoMoreShow(initialData.noMoreShow);

    // // 更新store中的groupInfo
    // setGlobalGroupInfo(newGroupInfo);

    // 重置无限滚动页码为当前页码
    setInfiniteScrollPage(currentPage);

    // 服务端数据更新后，关闭页面loading
    setIsGlobalLoading(false);
  }, [initialData, currentPage, setGroupInfo]);

  const { trackProductPitsExp, trackProductPitsClick, trackAdPitsExp, trackAdPitsClick } =
    useTrack();

  // 滚动到列表区域
  const scrollToListSection = useCallback(() => {
    const listSection = document.querySelector('.list-section');
    if (listSection) {
      const rect = listSection.getBoundingClientRect();
      const offsetTop = window.pageYOffset + rect.top - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  }, []);

  const headerData = {
    title: isMobileScreen ? goodsListData.title_m || goodsListData.title : goodsListData.title,
    description: isMobileScreen
      ? goodsListData.description_m || goodsListData.description
      : goodsListData.description,
  };

  // 构建新URL的工具函数
  const buildNewUrl = useCallback(
    (updates: { sort?: string; filter?: Record<string, any>; page?: number }) => {
      const newSearchParams = new URLSearchParams(searchParams || {});

      // 处理排序参数
      if (updates.sort !== undefined) {
        if (updates.sort && updates.sort !== DEFAULT_SORT_VALUE) {
          newSearchParams.set('sort', updates.sort);
        } else {
          newSearchParams.delete('sort');
        }
      }

      // 处理筛选参数
      if (updates.filter !== undefined) {
        newSearchParams.delete('filter');
        if (!isEmpty(updates.filter)) {
          const cleanFilter = {};
          Object.entries(updates.filter).forEach(([key, value]) => {
            if (value && key !== 'init') {
              cleanFilter[key] = value as string;
            }
          });
          if (!isEmpty(cleanFilter)) {
            newSearchParams.set('filter', JSON.stringify(cleanFilter));
          }
        }
      }

      // 处理页码参数
      if (updates.page !== undefined) {
        if (updates.page === 1) {
          newSearchParams.delete('page');
        } else {
          newSearchParams.set('page', updates.page.toString());
        }
      }

      const queryString = newSearchParams.toString();
      return `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    },
    [searchParams],
  );

  // 处理排序变化 - 通过URL变化触发服务端重渲染
  const handleSortChange = useCallback(
    (sortValue: { value: string | number; sortName: string }) => {
      const newSort = sortValue.value.toString();
      const newUrl = buildNewUrl({ sort: newSort, page: 1 });

      setIsGlobalLoading(true);
      scrollToListSection();
      router.push(newUrl, { scroll: false });

      // 埋点
      // sdProductListPageSortBtnClick(sortValue.sortName);
    },
    [buildNewUrl, router, scrollToListSection],
  );

  // 列数变化
  const handleColumnChange = useCallback(
    (newColumn: number) => {
      setColumn(newColumn);
      if (listData.length > 0) {
        scrollToListSection();
      }
    },
    [scrollToListSection, listData],
  );

  // 处理无限滚动到底部（仅WAP端）
  const handleReachBottom = useCallback(async () => {
    if (isWeb || loading || noMoreShow) return;

    const nextPage = infiniteScrollPage + 1;
    setInfiniteScrollPage(nextPage);
    setLoading(true);
    setIsShowFooterNav(false);

    try {
      // 构建API参数
      let sortParam = currentSort;
      if (sortParam === '' || sortParam === DEFAULT_SORT_VALUE) {
        sortParam = '';
      }
      const transformedFilter = transformFilterParams(currentFilter);

      const apiParams = {
        currency: currency.name,
        group_id: listId,
        limit,
        page: nextPage,
        order: sortParam,
        in_stock: 1,
        is_sale: 1,
        filter: transformedFilter,
        append_correlation: 1,
        show_adv: 1,
      };

      const response = (await fetchGoodsList(apiParams)) as unknown as {
        code: number;
        data: IProductListApiResponse;
      };

      if (response.code === 200) {
        const { list = [], all_total } = response.data as IProductListApiResponse;

        // 追加数据
        setListData((prev) => [...prev, ...list]);
        const _noMoreShow = all_total <= nextPage * 36;
        setNoMoreShow(_noMoreShow);
      }
    } catch (error) {
      logger.error('无限滚动加载失败:', error);
    } finally {
      setLoading(false);
    }
  }, [
    isWeb,
    loading,
    noMoreShow,
    infiniteScrollPage,
    currentSort,
    currentFilter,
    currency.name,
    listId,
  ]);

  // 处理筛选变化 - 通过URL变化触发服务端重渲染
  const handleFilterChange = useCallback(
    (newFilterResult: Record<string, any>) => {
      const newUrl = buildNewUrl({ filter: newFilterResult, page: 1 });

      setIsGlobalLoading(true);
      scrollToListSection();
      router.push(newUrl, { scroll: false });
    },
    [buildNewUrl, router, scrollToListSection],
  );

  // 商品曝光事件
  const handleProductClick = useCallback(
    (index: number, product: any) => {
      trackProductPitsClick(index, product);
    },
    [trackProductPitsClick],
  );

  // 筛选相关处理
  const handleFilterClick = useCallback(() => {
    // 打开筛选弹窗时，重置临时筛选状态为当前筛选状态
    setTempFilter(currentFilter);
    setShowFilter(true);
  }, [currentFilter, isWeb]);

  const handleFilterValueChange = useCallback(
    (filterId: string, value: any, isPopup: boolean) => {
      if (isPopup) {
        // 弹窗模式：更新临时筛选状态，提供立即的视觉反馈
        setTempFilter((prev) => ({ ...prev, [filterId]: value }));
      } else {
        // 非弹窗模式：立即应用筛选
        const newFilterResult = { ...(currentFilter || {}), [filterId]: value };
        handleFilterChange(newFilterResult);
      }
    },
    [currentFilter, handleFilterChange],
  );

  // 切换筛选项展开状态
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const handleFilterApply = useCallback(() => {
    handleFilterChange(tempFilter);
    setShowFilter(false);
  }, [tempFilter, handleFilterChange]);

  // 渲染筛选组件
  const renderGoodsFilter = useCallback(
    (isPopup = false) => {
      // 弹窗模式使用临时筛选状态，非弹窗模式使用当前筛选状态
      const filterValues = isPopup ? tempFilter : currentFilter;

      return (
        <GoodsFilter
          onClear={() => {
            if (isPopup) {
              // 弹窗模式：清空临时筛选状态
              setTempFilter({});
            } else {
              // 非弹窗模式：立即清空筛选
              handleFilterChange({});
            }
          }}
          values={filterValues}
          onValueChange={(filterId, value) => handleFilterValueChange(filterId, value, isPopup)}
          isWeb={isWeb}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          isPopup={isPopup}
          // sdProductListPageFilterSubmitBtnClick={sdProductListPageFilterSubmitBtnClick}
          {...(isPopup ? { onApply: handleFilterApply } : {})}
        />
      );
    },
    [
      tempFilter,
      currentFilter,
      handleFilterValueChange,
      handleFilterApply,
      handleFilterChange,
      isWeb,
      expandedSections,
      toggleSection,
    ],
  );

  // 处理分页变化 - 通过URL变化触发服务端重渲染
  const handlePageChange = useCallback(
    (page: number) => {
      const newUrl = buildNewUrl({ page });

      setIsGlobalLoading(true);
      scrollToListSection();
      router.push(newUrl, { scroll: false });
    },
    [buildNewUrl, router, scrollToListSection],
  );

  const handleFilterClose = useCallback(() => {
    // 关闭弹窗时重置临时筛选状态
    setTempFilter(currentFilter);
    setShowFilter(false);
  }, [currentFilter]);

  // 同步临时筛选状态与当前筛选状态
  useEffect(() => {
    setTempFilter(currentFilter);
  }, [currentFilter]);

  return (
    <div>
      <Header headerData={headerData} />

      <div className={`relative ${isWeb ? 'w-full min-h-screen flex px-[8vw]' : ''}`}>
        {/* 左侧筛选区域 - PC端 */}
        {/* TODO:这些组件都和商品列表一样，为啥不能复用呢？？ */}
        {isWeb && !isAcc && (
          <div className="max-[920px]:hidden">
            <div className="w-[288px] left-0 z-[110] h-screen sticky top-[140px] transition-transform duration-10">
              <div
                className="h-full bg-white overflow-y-auto show-scrollbar-when-hover"
                style={{ overscrollBehavior: 'contain' }}
              >
                <p className="pt-[16px] text-20 leading-[24px] font-bb pb-24 pl-48">Filters</p>
                <div className="pl-48 pr-24 pb-40">{renderGoodsFilter(false)}</div>
              </div>
            </div>
          </div>
        )}

        {/* 右侧内容区域 */}
        <div
          className={`
           goods-list
           ${isWeb && !isAcc ? 'min-h-screen w-[calc(100vw-304px)] max-[920px] ' : ''}
           ${isAcc && isWeb ? 'w-full max-w-[1640px] mx-auto' : ''}
          `}
        >
          <div className={`${isWeb ? 'px-40 pb-40' : ''} ${isAcc && isWeb ? 'mx-auto' : ''}`}>
            {/* 主要内容区域 */}
            <section className="list-section">
              {/* 排序筛选栏 */}
              <SortFilterBar
                listId={listId}
                currentSort={currentSort}
                onSortChange={handleSortChange}
                filterTotal={filterTotal}
                onFilterClick={handleFilterClick}
                showColumnControl={false}
                column={column}
                onColumnChange={handleColumnChange}
                isAcc={isAcc}
                groupInfo={groupInfo}
                isNewin={isNewin}
                isWeb={isWeb}
                inAppH5={inAppH5}
                total={total}
              />

              {/* 商品列表 */}
              <ProductList
                listData={listData}
                total={total}
                allTotal={allTotal}
                noData={noData}
                loading={loading}
                isWeb={isWeb}
                column={column}
                isAcc={isAcc}
                imageRule={imageRule}
                imageHoverRule={imageHoverRule}
                infiniteScrollPage={infiniteScrollPage}
                limit={limit}
                handleReachBottom={handleReachBottom}
                page={currentPage}
                handlePageChange={handlePageChange}
                handleProductClick={handleProductClick}
                handleProductInView={trackProductPitsExp}
                sdProductListPageADExp={trackAdPitsExp}
                sdProductListPageADClick={trackAdPitsClick}
                showNoMore={false}
              />
            </section>
          </div>
        </div>
      </div>

      {/* PC端筛选弹窗 */}
      {isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="left"
          width="482px"
          round={false}
          title="Filters"
          bodyClassName="!pt-0 !px-32"
          sx={{
            '& .popup-title': {
              textAlign: 'left',
              border: 'none',
              paddingInline: '32px !important',
              paddingBlock: '26px !important',
              lineHeight: '24px',
              '& .popup-title-text': {
                fontSize: '20px !important',
              },
            },
          }}
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}

      {/* 移动端筛选弹窗 */}
      {!isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="right"
          width="335px"
          round={false}
          title="Filters"
          bodyClassName="!pt-0"
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}
    </div>
  );
}
